// src/utils/evolutionTimeline.js
//
// Pure, framework-free assembly of an agent's evolution timeline from the agent
// doc — shared by the Agent Record sheet (AgentRecordSheet) and the Command
// Dashboard's Evolution preview card so the two surfaces can't drift. Ported
// from the Agent Hub's evolution tab via the record sheet (Closeout Spec §3.2).
//
// Entries cover only the event types with live writers: creation, consolidation
// cycles (agent.evolutionTimeline[] with a legacy synthesized fallback),
// lessons, scored games, and Forge strategy deploys. Drift/debrief entries stay
// out — nothing writes archetypeDrift or result-less memory reflections today.
// No React, no Firestore — unit-tested in evolutionTimeline.test.js.

import { CMD } from '../components/Dashboard/commandUI';
import { getArchetypeDisplayName } from '../data/archetypeDisplay';
// Pure, import-free (api/_utils/memoryLessons.js): the SAME definitions the
// consolidation filter uses, so the sheet and the wisdom rewrite agree on
// which rows are lessons (BUILD_RULES §9).
import { LEGACY_REFLECTION_FAILURE_LESSON, isNoLessonMarker, isRealLesson } from '../../api/_utils/memoryLessons.js';

// Wins keep the Hub's emerald — CMD reserves its red for downside, and draw
// stays deliberately neutral so only real losses read as red.
export const EMERALD = '#34D399';

// Reflection outage fix (P-OUT-1, display line D, 10 Oct 2026): until then a
// failed reflection was saved as a memory entry whose lesson was the literal
// LEGACY_REFLECTION_FAILURE_LESSON. Entries already holding it render the
// honest line below instead; no new entry can carry it. Part 2 (P-OUT-2, O9,
// 14 Oct 2026): a game that finished without a lesson now leaves a no-lesson
// marker (noLesson: true), which renders the same honest line.
export { LEGACY_REFLECTION_FAILURE_LESSON };
export const NO_LESSON_RECORDED_LINE = 'No lesson was recorded for this game.';

export const parseDate = (val) => {
  if (!val) return new Date(0);
  if (val?._seconds) return new Date(val._seconds * 1000);
  if (val?.toDate) return val.toDate();
  return new Date(val);
};

const estimateCycleDate = (createdAt, cycleNum, totalCycles) => {
  const start = parseDate(createdAt).getTime();
  const now = Date.now();
  const span = now - start;
  const fraction = cycleNum / (totalCycles + 1);
  return new Date(start + span * fraction);
};

// Friendly display names for the game-mode ids carried by agent.memory[]
// entries (api/agent/reflect.js writes battleDoc.gameMode — 'baggerbomb_agent'
// live, 'baggerbomb' legacy). Unknown ids are humanized (underscores stripped,
// title-cased) so a raw snake_case id never renders in a timeline title.
const GAME_MODE_LABEL = {
  baggerbomb_agent: 'BaggerBomb battle',
  baggerbomb: 'BaggerBomb battle',
};

const getGameModeDisplayName = (gameMode) => {
  if (!gameMode) return 'Battle';
  if (GAME_MODE_LABEL[gameMode]) return GAME_MODE_LABEL[gameMode];
  const humanized = String(gameMode)
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
  return `${humanized} battle`;
};

// "Today" / "Yesterday" / "3d ago" / "Mar 15" — the timeline's relative date.
export const formatRelativeDate = (date) => {
  if (!date || isNaN(date.getTime())) return '';
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/**
 * @param {Object|null} agent - the agent doc (reads createdAt, evolutionCycle,
 *   evolutionTimeline, consolidatedInsight, lessons, memory, deployedStrategy)
 * @returns {Array<Object>} timeline events, newest first. Each carries
 *   { type, title, subtitle, date, color } plus, for consolidation entries,
 *   { isConsolidation, eventId, narrative, metadata }.
 */
export function buildEvolutionTimeline(agent) {
  if (!agent) return [];
  const events = [];

  if (agent.createdAt) {
    events.push({
      type: 'creation',
      title: 'Agent created',
      subtitle: `${getArchetypeDisplayName(agent.archetype)} archetype`,
      date: parseDate(agent.createdAt),
      color: CMD.teal,
    });
  }

  // Prefer real entries from agent.evolutionTimeline[] (rich EvolutionEvents
  // from the consolidation writer); synthesize a muted entry only for cycles
  // that predate the writer.
  if (agent.evolutionCycle > 0) {
    const realByCycle = new Map();
    (agent.evolutionTimeline || []).forEach(ev => {
      if (ev?.type === 'consolidation' && Number.isInteger(ev?.cycle)) {
        realByCycle.set(ev.cycle, ev);
      }
    });

    for (let i = 1; i <= agent.evolutionCycle; i++) {
      const realEvent = realByCycle.get(i);
      if (realEvent) {
        events.push({
          type: 'evolution',
          isConsolidation: true,
          eventId: realEvent.id || `evo_cycle_${i}`,
          title: realEvent.headline || `Evolution cycle ${i} complete`,
          subtitle: realEvent.metadata?.keyShift || '',
          narrative: realEvent.narrative || null,
          metadata: realEvent.metadata || null,
          date: parseDate(realEvent.timestamp),
          color: CMD.allocation,
        });
      } else {
        events.push({
          type: 'evolution',
          isConsolidation: false,
          eventId: `evo_cycle_${i}_legacy`,
          title: `Evolution cycle ${i} complete`,
          subtitle: i === agent.evolutionCycle && agent.consolidatedInsight
            ? agent.consolidatedInsight.slice(0, 80) + '...'
            : 'Consolidated 5 games into strategic insight',
          date: estimateCycleDate(agent.createdAt, i, agent.evolutionCycle),
          color: CMD.teal,
        });
      }
    }
  }

  (agent.lessons || []).forEach(l => {
    if (!l?.createdAt) return;
    events.push({
      type: 'lesson',
      title: 'Lesson Learned',
      subtitle: l.text,
      date: parseDate(l.createdAt),
      color: CMD.gold,
    });
  });

  // A marker never stands in for a lesson: if the window also holds a real
  // lesson for the same game (the writers never put one beside the other), the
  // lesson's row is the game's row.
  const gamesWithLesson = new Set((agent.memory || []).filter(isRealLesson).map(m => m.gameId).filter(Boolean));
  (agent.memory || []).forEach(m => {
    if (!m.result) return;
    if (isNoLessonMarker(m) && gamesWithLesson.has(m.gameId)) return;
    const resultLabel = m.result === 'win' ? 'Win' : m.result === 'draw' ? 'Draw' : 'Loss';
    // "· +156" / "· −75" — signed with the typographic minus, dot-separated.
    const scoreLabel = Number.isFinite(m.score)
      ? ` · ${m.score > 0 ? '+' : m.score < 0 ? '−' : ''}${Math.abs(m.score)}`
      : '';
    events.push({
      type: 'game',
      title: `${getGameModeDisplayName(m.gameMode)} — ${resultLabel}${scoreLabel}`,
      subtitle: isNoLessonMarker(m) || m.lesson === LEGACY_REFLECTION_FAILURE_LESSON ? NO_LESSON_RECORDED_LINE : (m.lesson || ''),
      date: parseDate(m.date),
      color: m.result === 'win' ? EMERALD : m.result === 'draw' ? CMD.ink2 : CMD.risk,
    });
  });

  // Forge strategy deploy — written live by deployExperimentToAgent
  // (Forge → DeployToAgent), single entry from deployedStrategy metadata.
  if (agent.deployedStrategy?.deployedAt) {
    events.push({
      type: 'deploy',
      title: 'Strategy Deployed',
      subtitle: `"${agent.deployedStrategy.experimentName || 'Strategy'}" deployed from Forge`,
      date: parseDate(agent.deployedStrategy.deployedAt),
      color: EMERALD,
    });
  }

  return events.sort((a, b) => (b.date?.getTime?.() || 0) - (a.date?.getTime?.() || 0));
}
